import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, Mail } from "lucide-react";
import { CrestLogo } from "../components/CrestLogo";

const CONTENT = {
  waitlist: {
    eyebrow: "Expression of Interest Received",
    heading: "You’ve pulled up a seat.",
    body: [
      "Thank you for your interest in The Catalyst Table.",
      "We are an intentionally curated dining community where remarkable women gather to share ideas, build meaningful relationships and open doors for one another.",
      "Your expression of interest has been received, and we will be in touch as new dinners and opportunities become available.",
    ],
    note: "A confirmation has been sent to your inbox.",
  },
  nominate: {
    eyebrow: "Nomination Received",
    heading: "Thank you for passing a seat forward.",
    body: [
      "Thank you for nominating a remarkable woman to The Catalyst Table.",
      "Every introduction helps us grow a community built on generosity, thoughtful connection and women opening doors for women.",
    ],
    note: "A confirmation has been sent to your inbox.",
  },
};

export default function ThankYou() {
  const [params] = useSearchParams();
  const type = params.get("type") === "nominate" ? "nominate" : "waitlist";
  const name = params.get("name");
  const c = CONTENT[type];

  return (
    <div
      className="bg-[#F7F5F2] min-h-screen flex flex-col selection:bg-[#1F3A33] selection:text-[#F7F5F2]"
      data-testid="thank-you-page"
    >
      {/* Header */}
      <header className="h-20 flex items-center border-b border-[#1F3A33]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 w-full flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <CrestLogo variant="dark" className="w-12 h-12 md:w-10 md:h-10" />
            <span className="font-serif tracking-[0.15em] text-base md:text-lg font-light text-[#1F3A33] uppercase">
              The Catalyst Table
            </span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-xs font-semibold tracking-widest text-[#1F3A33] uppercase hover:text-[#C8A97E] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Home</span>
          </Link>
        </div>
      </header>

      {/* Body */}
      <main className="flex-grow flex items-center justify-center px-6 py-20 md:py-28">
        <div className="max-w-2xl w-full text-center space-y-10 animate-[fadeup_0.9s_ease-out]">
          <div className="flex justify-center">
            <CrestLogo variant="dark" className="w-24 h-24 md:w-28 md:h-28" />
          </div>

          <div className="space-y-5">
            <span className="uppercase text-xs tracking-[0.28em] font-semibold text-[#8B6A4E] block">
              {c.eyebrow}
            </span>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light text-[#1F3A33] leading-tight" data-testid="thank-you-heading">
              {c.heading}
            </h1>
            <div className="w-12 h-[1px] bg-[#C8A97E] mx-auto"></div>
          </div>

          <div className="space-y-5 text-base md:text-lg font-sans font-light leading-relaxed text-[#2E2E2E] max-w-xl mx-auto">
            {name ? (
              <p className="text-[#1F3A33] font-medium">Dear {name},</p>
            ) : null}
            {c.body.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>

          <div className="inline-flex items-center gap-2 text-xs tracking-widest uppercase text-[#8B6A4E] border border-[#C8A97E]/40 px-5 py-3">
            <Mail className="w-4 h-4 text-[#C8A97E]" /> {c.note}
          </div>

          <div className="pt-4 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/"
              data-testid="thank-you-home-button"
              className="bg-[#1F3A33] text-[#F7F5F2] uppercase text-xs font-semibold tracking-[0.15em] px-8 py-4 hover:bg-[#2E2E2E] transition-all duration-300 flex items-center justify-center gap-3"
            >
              Return Home <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/#dinners"
              className="border border-[#C8A97E] text-[#1F3A33] uppercase text-xs font-semibold tracking-[0.15em] px-8 py-4 hover:bg-[#C8A97E]/10 transition-all duration-300 flex items-center justify-center"
            >
              Explore Upcoming Dinners
            </Link>
          </div>

          <p className="font-serif italic text-lg text-[#8B6A4E] pt-6">
            Pull up a seat. Leave with momentum.
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#1F3A33] text-[#F7F5F2]/60 py-6 border-t border-[#C8A97E]/20 text-center text-xs">
        <p>Women intentionally opening doors for one another.</p>
      </footer>
    </div>
  );
}
